import { useTranslation } from 'react-i18next';

import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, Eye, EyeOff, Pencil, TrashIcon } from 'lucide-react';

import type { KeyView } from '@codes/openapi-keys';
import { cn } from '@codes/ui-kit';

import { MaskedKeyDisplay } from './MaskedKeyDisplay';

interface OpenApiKeyCardProps {
    data: KeyView;
    onEdit: (key: KeyView) => void;
    onDelete: (key: KeyView) => void;
    onRegenerate: (key: KeyView) => void;
    onValidate: (key: KeyView) => void;
}

export const OpenApiKeyCard = ({ data, onEdit, onDelete, onRegenerate, onValidate }: OpenApiKeyCardProps) => {
    const { t } = useTranslation();

    const isInvalid = data.invalid;

    return (
        <Card className="group transition-all hover:shadow-md hover:border-point">
            <CardHeader className="p-4 pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <CardTitle className="text-base font-semibold truncate mb-1">
                            {data.name || data.id || t('openApiKeys.card.unnamedKey')}
                        </CardTitle>
                        {data.createdAt && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>
                                    {t('openApiKeys.list.createdAt')}:{' '}
                                    {formatDistanceToNow(new Date(data.createdAt), { addSuffix: true })}
                                </span>
                            </div>
                        )}
                    </div>
                    <Badge variant={isInvalid ? 'secondary' : 'default'} className="flex items-center gap-1 shrink-0">
                        <div className={cn('h-2 w-2 rounded-full', isInvalid ? 'bg-red-500' : 'bg-green-500')} />
                        {isInvalid ? t('openApiKeys.card.status.invalid') : t('openApiKeys.card.status.active')}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
                <MaskedKeyDisplay value={data.apiKey || ''} label="" />

                <div className="flex items-center justify-between text-xs pt-2">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                        {data.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        <span>
                            {data.hidden
                                ? t('openApiKeys.card.visibility.hidden')
                                : t('openApiKeys.card.visibility.visible')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onValidate(data)}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t('openApiKeys.validation.validate')}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onEdit(data)}>
                            <Pencil className="h-3 w-3 mr-1" />
                            {t('openApiKeys.form.edit')}
                        </Button>
                        {/* TODO: API 준비되면 활성화 */}
                        {/* <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => onRegenerate(data)}
                        >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            {t('openApiKeys.regenerate')}
                        </Button> */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(data)}
                        >
                            <TrashIcon className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
